import {
  CanvasTexture,
  DoubleSide,
  Mesh,
  MeshBasicMaterial,
  Object3D,
  PlaneGeometry,
  Quaternion,
  SRGBColorSpace,
  Vector3,
} from "three";

import type { ContentArtwork } from "./contracts";

const LABEL_WIDTH = 0.44;
const LABEL_HEIGHT = 0.2;
const ANCHOR_EDGE_OFFSET = 0.08;
const FRAME_GAP = 0.05;

export function createArtworkLabel(
  artwork: ContentArtwork,
  painting: Object3D,
  anchor: Object3D,
): Mesh<PlaneGeometry, MeshBasicMaterial> {
  const texture = new CanvasTexture(drawLabel(artwork));
  texture.colorSpace = SRGBColorSpace;
  texture.anisotropy = 4;

  const label = new Mesh(
    new PlaneGeometry(LABEL_WIDTH, LABEL_HEIGHT),
    new MeshBasicMaterial({
      map: texture,
      side: DoubleSide,
      toneMapped: false,
    }),
  );
  label.name = `viewer_label_${artwork.id}`;
  label.userData = {
    artwork_id: artwork.id,
    title: artwork.title,
    interaction_kind: "artwork_label",
    interactive: true,
  };

  const paintingPosition = painting.getWorldPosition(new Vector3());
  const labelPosition = anchor.getWorldPosition(new Vector3());
  const outward = labelPosition.clone().sub(paintingPosition);
  outward.y = 0;
  if (outward.lengthSq() === 0) throw new Error(`GLB_LABEL_POSITION_INVALID: ${artwork.id}`);
  outward.normalize();
  labelPosition.addScaledVector(
    outward,
    LABEL_WIDTH / 2 - ANCHOR_EDGE_OFFSET + FRAME_GAP,
  );
  labelPosition.y += LABEL_HEIGHT / 2;
  label.position.copy(labelPosition);
  label.quaternion.copy(painting.getWorldQuaternion(new Quaternion()));
  return label;
}

function drawLabel(artwork: ContentArtwork): HTMLCanvasElement {
  const canvas = document.createElement("canvas");
  canvas.width = 880;
  canvas.height = 400;
  const context = canvas.getContext("2d");
  if (context === null) throw new Error("ARTWORK_LABEL_CANVAS_UNAVAILABLE");

  context.fillStyle = "#eee9dc";
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.fillStyle = "#b6934c";
  context.fillRect(0, 0, 14, canvas.height);
  context.strokeStyle = "#bdb4a2";
  context.lineWidth = 5;
  context.strokeRect(2.5, 2.5, canvas.width - 5, canvas.height - 5);

  context.textBaseline = "top";
  context.fillStyle = "#6d665b";
  context.font = "700 25px Inter, Arial, sans-serif";
  context.fillText(`${String(artwork.route_order).padStart(2, "0")} / 08`, 54, 42);
  context.textAlign = "right";
  context.fillStyle = "#8a6c31";
  context.fillText("DETALLE  ↗", 826, 42);

  context.textAlign = "left";
  context.fillStyle = "#211f1b";
  context.font = "600 49px Georgia, 'Times New Roman', serif";
  const lines = wrapLines(context, artwork.title, 772, 2);
  lines.forEach((line, index) => context.fillText(line, 54, 105 + index * 56));

  context.fillStyle = "#5e584e";
  context.font = "400 28px Inter, Arial, sans-serif";
  context.fillText(artwork.author, 54, 300);
  return canvas;
}

function wrapLines(
  context: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
  maxLines: number,
): string[] {
  const words = text.trim().split(/\s+/u);
  const lines: string[] = [];
  for (const word of words) {
    const index = Math.max(lines.length - 1, 0);
    const current = lines[index] ?? "";
    const candidate = current === "" ? word : `${current} ${word}`;
    if (current === "" || context.measureText(candidate).width <= maxWidth) {
      lines[index] = candidate;
    } else if (lines.length < maxLines) {
      lines.push(word);
    } else {
      lines[index] = ellipsize(context, candidate, maxWidth);
      break;
    }
  }
  return lines;
}

function ellipsize(context: CanvasRenderingContext2D, text: string, maxWidth: number): string {
  let value = text;
  while (value.length > 1 && context.measureText(`${value}…`).width > maxWidth) {
    value = value.slice(0, -1).trimEnd();
  }
  return `${value}…`;
}
